import React, { ChangeEvent, FC, useCallback, useState } from 'react';

import { Box } from '@rbilabs/component-library';
import { defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import ActionButton from 'components/action-button';
import { FloatingLabelInput } from 'components/floating-label-fields';
import {
  GetLoyaltyCardsDocument,
  ILoyaltyCard,
  useAddVoucherMutation,
  useMergeLoyaltyCardsMutation
} from 'generated/rbi-graphql';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { parseGraphQLErrorCodes } from 'utils/errors';
import { GraphQLErrorCodes } from 'utils/errors/types';
import { mutationDidSucceed } from 'utils/graphql';

import { cleanLoyaltyCardId, formatLoyaltyCardId } from '../utils';

const Anchor = styled.a`
  &:target {
    position: relative;
    top: -360px;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 0 2rem 0;
  text-align: center;
`;

const InputInner = styled.div`
  width: min(400px, 100%);
  margin-top: 2rem;
`;

const NormalText = styled.p`
  font-family: ${Styles.fontFamily.base};
  font-weight: ${Styles.fontWeight.heavy};
  font-size: 1rem;
  line-height: 1.56;
  margin: 1rem;
  text-align: center;
`;

const SuccessHeading = styled.h4`
  color: ${Styles.color.validated};
  font-size: 1.5rem;
  line-height: 1.56;
  margin: 1rem 1rem 0;
  text-align: center;
`;

const SuccessText = styled(NormalText)`
  color: ${Styles.color.validated};
  margin: 0.5rem 1rem 1rem;
`;

const ErrorText = styled(NormalText)`
  color: ${Styles.color.primary};
`;

const StrongText = styled.strong`
  font-weight: ${Styles.fontWeight.black};
`;

const ErrorAnchor = styled.a`
  color: ${Styles.color.primary};
`;

const CARD_ID_PLACEHOLDER = 'XXXX-XXXX-XXXX';

const messages = defineMessages({
  above: {
    id: 'above',
    defaultMessage: 'above',
    description: 'location of loyalty card input'
  },

  below: {
    id: 'below',
    defaultMessage: 'below',
    description: 'location of promo card input'
  },

  linkMyCard: {
    id: 'linkMyCard',
    defaultMessage: 'Link my card',
    description: 'Link a physical card'
  },

  loyaltyCardValidationError: {
    id: 'loyaltyCardValidationError',
    defaultMessage:
      "This doesn't look like a valid card number like (S89Y-U6GH-9YNN), please check your card and try again.",
    description: 'Error message while validating loyalty card'
  },

  yourCardHasBeenSuccessfullyRegistered: {
    id: 'yourCardHasBeenSuccessfullyRegistered',
    defaultMessage: 'Your card has been successfully registered.',
    description: 'Successful registration message'
  },

  yay: {
    id: 'yay',
    defaultMessage: 'Yay!',
    description: 'Success message'
  },

  enterCardNumber: {
    id: 'enterCardNumber',
    defaultMessage: 'Enter card number',
    description: 'Text label to enter card'
  },

  chatWithUs: {
    id: 'chatWithUs',
    defaultMessage: 'chat with us',
    description: 'Chat with us callout'
  },

  loyaltyCardAlreadyLinked: {
    id: 'loyaltyCardAlreadyLinked',
    defaultMessage: 'This card has already been linked to an account.',
    description:
      'Error message to show when merging a loyalty card fails due to the card already being linked'
  },

  loyaltyCardMergeFailed: {
    id: 'loyaltyCardMergeFailed',
    defaultMessage:
      "We're sorry we couldn't link your card at this time. Please check if your card is already linked to another account. If not, check your connection and try again. Or, {link} for assistance.",
    description: 'Error message to show when merging a loyalty card fails for an unspecified reason'
  },

  promoCodeApplied: {
    id: 'promoCodeApplied',
    defaultMessage:
      'Promo code applied successfully! Check the Rewards & Offers section on the home page',
    description: 'Success Message informing user promo code was applied'
  },

  promoCodeAppliedPage: {
    id: 'promoCodeAppliedPage',
    defaultMessage: 'Rewards & Offers',
    description: 'Page to check to see applied voucher'
  },

  promoCodeLabel: {
    id: 'promoCodeLabel',
    defaultMessage: 'Enter promo code',
    description: 'label for promo code input field'
  },

  somethingWentWrong: {
    id: 'somethingWentWrong',
    defaultMessage: 'Something went wrong',
    description: 'Error message to show that something went wrong'
  },

  tryingToEnterPromoCode: {
    id: 'tryingToEnterPromoCode',
    defaultMessage: 'Trying to enter a Promo Code? Enter it {below}.',
    description: 'Message to user where to enter promo code'
  },

  voucherCodeError: {
    id: 'voucherCodeError',
    defaultMessage: 'Please enter a Promo code',
    description: 'Error message to show code entered is not valid or missing'
  },

  voucherNotFound: {
    id: 'voucherNotFound',
    defaultMessage:
      'Promo code invalid or expired. Trying to Link your Physical Card? Enter it {above}.',
    description: 'Error message to show when voucher can not be found'
  },

  voucherUsed: {
    id: 'voucherUsed',
    defaultMessage: 'This Promo code has already been used',
    description: 'Error message for an already used voucher'
  }
});

export type IRegistrationProps = {
  activeCard: ILoyaltyCard | null;
  buttonText?: string;
  isVoucher?: boolean;
  enableVoucher?: boolean;
};

enum InputId {
  PROMO = 'promoCodeInput',
  LOYALTY = 'loyaltyCardInput'
}

const Registration: FC<IRegistrationProps> = ({
  activeCard,
  buttonText,
  isVoucher,
  enableVoucher
}) => {
  const { formatMessage, locale } = useIntl();
  const { logEvent } = useMParticleContext();

  const logRegistrationEvent = useCallback(
    (success: boolean, message?: string) => {
      const eventName = isVoucher
        ? CustomEventNames.PROMO_CARD_REGISTERED
        : CustomEventNames.PHYSICAL_CARD_REGISTERED;

      logEvent(eventName, EventTypes.UserContent, {
        Response: success ? 'Successful' : 'Failure',
        'Response Description': success ? 'Successful' : message
      });
    },
    [isVoucher, logEvent]
  );

  const [addVoucherMutation, addVoucherMutationResult] = useAddVoucherMutation();
  const { loading: addVoucherLoading, error: addVoucherError } = addVoucherMutationResult;
  const addVoucherSucceeded = mutationDidSucceed(addVoucherMutationResult);
  const addVoucherErrorCodes = addVoucherError ? parseGraphQLErrorCodes(addVoucherError) : null;
  const addVoucherErrorMessage =
    {
      [GraphQLErrorCodes.VOUCHER_NOT_FOUND]: messages.voucherNotFound,
      [GraphQLErrorCodes.VOUCHER_USED]: messages.voucherUsed,
      [GraphQLErrorCodes.VOUCHER_ERROR]: messages.somethingWentWrong
    }[addVoucherErrorCodes?.[0]?.errorCode ?? ''] || messages.somethingWentWrong;

  const [fireMergeLoyaltyCardsMutation, mutationResult] = useMergeLoyaltyCardsMutation({
    refetchQueries: [{ query: GetLoyaltyCardsDocument }]
  });
  const { error: mergeLoyaltyCardsError, loading: mergeLoyaltyCardsLoading } = mutationResult;

  const errorCodes = mergeLoyaltyCardsError ? parseGraphQLErrorCodes(mergeLoyaltyCardsError) : null;
  const errorMessage =
    errorCodes && errorCodes[0].errorCode === GraphQLErrorCodes.LOYALTY_CARD_ALREADY_LINKED_ERROR
      ? messages.loyaltyCardAlreadyLinked
      : messages.loyaltyCardMergeFailed;

  const mergeLoyaltySucceeded = mutationDidSucceed(mutationResult);

  const requestFailedErrorMessage = formatMessage(errorMessage, {
    link: (
      <ErrorAnchor
        href={`https://global.knowledgeforce.com/casemanagement/form/timhortons?lang=${locale}&app-version=N/A`}
      >
        {formatMessage(messages.chatWithUs)}
      </ErrorAnchor>
    )
  });

  const [physicalCardId, setPhysicalCardId] = useState('');
  const [formattedPhysicalCardId, setFormattedPhysicalCardId] = useState('');
  const [physicalCardIdValid, setPhysicalCardIdValid] = useState(true);
  const [showVoucherErrorText, setShowVoucherErrorText] = useState(false);

  const formatCardId = useCallback(cardId => {
    // remove all non alpha numeric characters
    const rawValue = cleanLoyaltyCardId(cardId);
    // adds hyphen every 4 characters
    const formattedValue = formatLoyaltyCardId(rawValue);
    return [rawValue, formattedValue];
  }, []);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const cardId = event.target.value.toUpperCase();
      const [rawCardIdValue, formattedCardIdValue] = formatCardId(cardId);

      setPhysicalCardIdValid(rawCardIdValue.length === 12);
      setFormattedPhysicalCardId(formattedCardIdValue);
      setShowVoucherErrorText(false);
      setPhysicalCardId(cardId);
    },
    [setPhysicalCardIdValid, setPhysicalCardId, formatCardId]
  );

  const { cardId: activeCardId = '' } = activeCard || {};
  const handleMergeLoyaltyCards = useCallback(async () => {
    if (!physicalCardIdValid && isVoucher) {
      setShowVoucherErrorText(true);
      return;
    }

    if (!physicalCardId || !physicalCardIdValid || !activeCardId) {
      return;
    }

    const cleanedLoyaltyCardId = cleanLoyaltyCardId(physicalCardId);
    try {
      if (isVoucher) {
        await addVoucherMutation({ variables: { voucherId: cleanedLoyaltyCardId } });
      } else {
        await fireMergeLoyaltyCardsMutation({
          variables: { cardId: cleanedLoyaltyCardId, existingCardId: activeCardId }
        });
      }
      logRegistrationEvent(true);
    } catch (error) {
      logRegistrationEvent(false, error.message);
    }
  }, [
    physicalCardId,
    physicalCardIdValid,
    activeCardId,
    isVoucher,
    addVoucherMutation,
    fireMergeLoyaltyCardsMutation,
    logRegistrationEvent
  ]);

  useEffectOnUpdates(() => {
    if (mergeLoyaltySucceeded) {
      setPhysicalCardId('');
      setPhysicalCardIdValid(true);
    }
  }, [mergeLoyaltySucceeded]);

  return (
    <Container>
      <InputInner>
        <Anchor id={isVoucher ? InputId.PROMO : InputId.LOYALTY} />
        <FloatingLabelInput
          label={formatMessage(isVoucher ? messages.promoCodeLabel : messages.enterCardNumber)}
          placeholder={CARD_ID_PLACEHOLDER}
          value={formattedPhysicalCardId}
          onChange={handleChange}
        />
      </InputInner>
      {mergeLoyaltySucceeded && (
        <>
          <SuccessHeading>{formatMessage(messages.yay)}</SuccessHeading>
          <SuccessText>{formatMessage(messages.yourCardHasBeenSuccessfullyRegistered)}</SuccessText>
        </>
      )}
      {addVoucherSucceeded && (
        <SuccessText>
          {formatMessage(messages.promoCodeApplied, {
            page: <StrongText>{formatMessage(messages.promoCodeAppliedPage)}</StrongText>
          })}
        </SuccessText>
      )}
      {isVoucher
        ? showVoucherErrorText && <ErrorText>{formatMessage(messages.voucherCodeError)}</ErrorText>
        : !physicalCardIdValid && (
            <ErrorText>{formatMessage(messages.loyaltyCardValidationError)}</ErrorText>
          )}
      {mergeLoyaltyCardsError && (
        <>
          <ErrorText>{requestFailedErrorMessage}</ErrorText>
          {enableVoucher && (
            <ErrorText>
              {formatMessage(messages.tryingToEnterPromoCode, {
                below: <a href={`#${InputId.PROMO}`}>{formatMessage(messages.below)}</a>
              })}
            </ErrorText>
          )}
        </>
      )}
      {addVoucherError && !showVoucherErrorText && (
        <ErrorText>
          {formatMessage(addVoucherErrorMessage, {
            above: <a href={`#${InputId.LOYALTY}`}>{formatMessage(messages.above)}</a>
          })}
        </ErrorText>
      )}
      <Box width="100%" margin="1.5rem 0 0" center>
        <ActionButton
          isLoading={mergeLoyaltyCardsLoading || addVoucherLoading}
          onClick={handleMergeLoyaltyCards}
        >
          {buttonText || formatMessage(messages.linkMyCard)}
        </ActionButton>
      </Box>
    </Container>
  );
};

export default Registration;
